import { DetailApi } from '@/domains/project/apis/detail-api';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { ReportApi } from '../apis/report-api';
import { EStreamStatus, ReportCatalogStatus } from '../types';
import type {
  ReportResponse,
  ReportDetail,
  IProjectDetail,
  ReportCatalog,
  ReportResponseData,
  ReportInfo,
  ReportTitle,
  ReportOutline,
  ReportContent,
  ReportUpdateParams,
  IDocument,
} from '../types';

export interface ReportService {
  initData: Partial<ReportDetail>;
  projectDetail: IProjectDetail;
  loading: boolean;
  contents: ReportCatalog[];
  activeSection: string;
  reportReferenceMap: Map<string, IDocument>;
  getProjectDetail(projectId: string): Promise<IProjectDetail>;
  generateReport(projectId: string, isRegenerate?: boolean): Promise<void>;
  updateReport(params: ReportUpdateParams): Promise<ReportTitle | ReportContent>;
  updateActiveSection(section: string): void;
  getReportReference(projectId: string, refId: string): Promise<IDocument | undefined>;
}

export const ReportService = Symbol('ReportService');

@injectable()
export class ReportServiceImpl implements ReportService {
  private _initData: Partial<ReportDetail> = {};
  private _projectDetail: IProjectDetail = {};
  private _loading = false;

  private _contents: ReportCatalog[] = [];

  private _activeSection = '';

  private _reportReferenceMap: Map<string, IDocument> = new Map();

  // eslint-disable-next-line no-useless-constructor
  constructor(
    @inject(ReportApi) private readonly _reportApi: ReportApi,
    @inject(DetailApi) private readonly _detailApi: DetailApi,
  ) {}

  get projectDetail() {
    return this._projectDetail;
  }

  get initData() {
    return this._initData;
  }

  get loading() {
    return this._loading;
  }

  get contents() {
    return this._contents;
  }

  get activeSection() {
    return this._activeSection;
  }

  get reportReferenceMap() {
    return this._reportReferenceMap;
  }

  async getProjectDetail(projectId: string): Promise<IProjectDetail> {
    return this._detailApi.getProjectDetail(projectId).then((projectDetail) => {
      this._projectDetail = projectDetail;
      return projectDetail;
    });
  }

  clearData() {
    this._activeSection = '';
    this._contents = [];
    this._initData = {};
  }

  handleGenerateProcessing(projectId: string, message?: ReportResponse) {
    this._initData = {
      ...this._initData,
      id: projectId,
      completed_time: (message as ReportInfo)?.spend_time || this._initData?.completed_time,
      time_saved: (message as ReportInfo)?.time_saved || this._initData?.time_saved,
      title: (message as ReportTitle)?.title?.text || this._initData?.title,
      sections: (message as ReportOutline)?.outline || this._initData?.sections,
    };
    if (
      this._initData?.sections &&
      !(message as ReportOutline)?.outline &&
      !this._contents?.length
    ) {
      this._contents = (this._initData.sections || [])?.map((section) => {
        return {
          id: section.index,
          status: ReportCatalogStatus.PENDING,
          title: section.parent_topic,
          content: '',
        };
      });
    }
    const phraseMsg = (message as ReportContent)?.phrase;
    if (phraseMsg) {
      const phraseText = phraseMsg.text;
      const phraseTextWidthIndex = phraseMsg.label_text;
      const phraseIndex = phraseMsg.index;
      if (this._activeSection && this._activeSection !== phraseIndex) {
        const lastSection = this._contents.find((ele) => ele.id === this._activeSection);
        if (lastSection) {
          lastSection.status = ReportCatalogStatus.DONE;
        }
      }
      this._activeSection = phraseIndex || '';
      const section = this._contents.find((ele) => ele.id === phraseIndex);
      if (section) {
        if (phraseText) {
          section.content = phraseText;
        } else if (phraseTextWidthIndex) {
          section.content = phraseTextWidthIndex;
        }
      }
    }
  }

  private async generateSectionReport(projectId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const sse = this._reportApi.getReportStream<ReportResponseData>(projectId);

      return sse
        .onOpen(() => {
          console.log('Connection opened');
          this.clearData();
        })
        .onMessage(({ data }) => {
          const { message, status } = data;
          if (status === EStreamStatus.FAILED) {
            reject(message);
          } else if (status === EStreamStatus.PROCESSING) {
            this.handleGenerateProcessing(projectId, message);
          }
        })
        .onFinish(() => {
          console.log('✅ Done');
          const section = this._contents.find((ele) => ele.id === this._activeSection);
          if (section) {
            section.status = ReportCatalogStatus.DONE;
          }
          resolve();
        })
        .onError((error) => {
          console.error('❌ Error', error);
          // section.status = ReportCatalogStatus.ERROR;
          reject(error);
        })
        .start();
    });
  }

  // 生成完整报告
  async generateReport(projectId: string, isRegenerate = false): Promise<void> {
    try {
      this._loading = true;
      if (isRegenerate) {
        await this._reportApi.refreshReport(projectId);
        this.clearData();
      }
      await this.generateSectionReport(projectId);
    } finally {
      this._loading = false;
    }
  }

  async updateReport(params: ReportUpdateParams): Promise<ReportTitle | ReportContent> {
    return this._reportApi.updateReport(params).then((res) => {
      if ((res as ReportTitle).title) {
        this._initData.title = (res as ReportTitle).title?.text || '';
      } else if ((res as ReportContent).phrase) {
        const phraseMsg = (res as ReportContent).phrase;
        const phraseText = phraseMsg?.text;
        const phraseIndex = phraseMsg?.index;
        const section = this._contents.find((ele) => ele.id === phraseIndex);
        if (section) {
          section.content = phraseText;
        }
      }
      return res;
    });
  }

  async getReportReference(projectId: string, refId: string): Promise<IDocument | undefined> {
    return this._reportApi.getReportReference(projectId, refId).then((res) => {
      this._reportReferenceMap.set(refId, res);
      return res;
    });
  }

  updateActiveSection(section: string) {
    this._activeSection = section;
  }
}
